import { useState } from "react";

import { capLabel, type McpQuotaDelete } from "../../api/mcp";
import { useAccess } from "../../auth/useAccess";
import type { AppConfig } from "../../config";
import { Button } from "../../f7";
import { useWrite } from "../../hooks/useWrite";
import { ConfirmDialog } from "../write/ConfirmDialog";
import { Badge, Card, Notice, Panel, Table, Td, Th } from "../ui";

/**
 * Remove a tool's own cap row so it falls back to the tier's `*` default.
 *
 * `DELETE /admin/quota/{tier}/{tool}` was mounted and unreachable — the editor
 * could set a cap but never take one away, so an override added by mistake
 * could only be overwritten with another number, never actually removed.
 *
 * TWO THINGS THE API IS EXPLICIT ABOUT, and both shape this UI:
 *
 * `now_resolves_to` comes back on the response and is what the tool ACTUALLY
 * falls back to. The success message is computed from that, never from the
 * tier's `*` — because for the `*` row itself the two differ, which is the
 * whole reason the next paragraph exists.
 *
 * Deleting `*` is guarded. Without it every tool with no explicit row falls
 * through to the unknown-tier path, which resolves to the FREE tier's map —
 * a different tier's commercial terms, not "no limit". That is why removing it
 * is type-to-confirm as well as guarded: the 409 explains, and the operator
 * still has to mean it.
 */
export function RemoveOverride({
  config,
  tier,
  caps,
  onDone,
}: {
  config: AppConfig;
  tier: string;
  /** tool → cap. Only entries with their OWN row can be removed. */
  caps: Record<string, number>;
  onDone: () => void;
}) {
  const access = useAccess();
  const write = useWrite<McpQuotaDelete>(config);
  const [target, setTarget] = useState<string | null>(null);
  const [done, setDone] = useState<McpQuotaDelete | null>(null);

  const canWrite = access.can("quota:write");
  const canForce = access.can("quota:force");
  const rows = Object.keys(caps).sort((a, b) => (a === "*" ? -1 : b === "*" ? 1 : a.localeCompare(b)));

  function submit(reason: string, force = false) {
    if (!target) return;
    void write
      .submit(`/mcp/admin/quota/${encodeURIComponent(tier)}/${encodeURIComponent(target)}`, {
        method: "DELETE",
        // `force` is a QUERY param on the quota routes, never a body field.
        query: force ? { force: true } : undefined,
        body: JSON.stringify({ reason }),
      })
      .then((r) => {
        if (!r) return;
        setDone(r);
        setTarget(null);
        onDone();
      });
  }

  if (rows.length === 0) return null;

  return (
    // A Card, like every other panel. The `text-xs uppercase` heading is the
    // console's field-label style; used as a section title it left this table
    // unbounded on the page, directly under another unbounded table.
    <Card
      title="Remove an override"
      note="A tool with its own row. Removing it makes the tool resolve through the tier default again."
      readOnly={!canWrite}
    >
      {done && (
        <div className="mb-4">
          <Notice tone="green" title="Removed, and read back">
            <p>
              <span className="ref">{done.tool}</span> no longer has its own row on{" "}
              <span className="ref">{done.tier}</span> — it was{" "}
              {capLabel(done.previous).text.toLowerCase()} and now resolves to{" "}
              <strong>{capLabel(done.now_resolves_to).text}</strong>.
            </p>
            <p className="mt-1 text-xs">
              Effective within ~{Math.round(done.effective_within_seconds / 60)} minutes — the cap
              cache TTL, not instantly.
            </p>
          </Notice>
        </div>
      )}

      <Table
        className="[--gutter:0px]"
        head={
          <tr>
            <Th>Tool</Th>
            <Th>Cap</Th>
            <Th right>{null}</Th>
          </tr>
        }
      >
        {rows.map((tool) => {
          const c = capLabel(caps[tool]!);
          return (
            <tr key={tool}>
              <Td>
                <span className="ref">{tool}</span>
                {tool === "*" && (
                  <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
                    the tier default
                  </span>
                )}
              </Td>
              <Td>
                <Badge tone={c.tone}>{c.text}</Badge>
              </Td>
              <Td right>
                {canWrite && (
                  <Button
                    plain
                    type="button"
                    onClick={() => {
                      write.reset();
                      setDone(null);
                      setTarget(tool);
                    }}
                  >
                    Remove
                  </Button>
                )}
              </Td>
            </tr>
          );
        })}
      </Table>

      {!canWrite && (
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
          Removing a row needs <span className="ref">quota:write</span>, granted by admin-operator
          or admin-owner.
        </p>
      )}

      <ConfirmDialog
        open={target !== null}
        title={`Remove ${target} from ${tier}`}
        confirmLabel="Remove the row"
        destructive={target === "*"}
        // The `*` row is one of the few type-to-confirm cases: removing it does
        // not mean "no limit", it means every uncapped tool on this tier starts
        // resolving through the FREE tier's map.
        typeToConfirm={target === "*" ? tier : undefined}
        busy={write.state.kind === "submitting"}
        error={
          write.state.kind === "notfound" ? (
            <Notice tone="amber" title="No such row">
              <p>
                Nothing to remove — <span className="ref">{target}</span> has no own row on this
                tier, so it already resolves through <span className="ref">*</span>.
              </p>
            </Notice>
          ) : write.state.kind === "denied" || write.state.kind === "invalid" ? (
            <Notice tone="red" title="Rejected">
              <p>{write.state.error.message}</p>
            </Notice>
          ) : null
        }
        onConfirm={(reason) => submit(reason)}
        onCancel={() => {
          setTarget(null);
          write.reset();
        }}
      >
        {target === "*" ? (
          <Panel className="p-3">
            <p className="text-sm">
              Removing the <span className="ref">*</span> row does <strong>not</strong> make this
              tier unlimited. Every tool without its own row falls through to the unknown-tier
              path, which resolves to the <span className="ref">free</span> tier&apos;s map — a
              different tier&apos;s commercial terms.
            </p>
          </Panel>
        ) : (
          <p className="text-sm">
            <span className="ref">{target}</span> loses its own cap and falls back to this
            tier&apos;s <span className="ref">*</span> default. The response says what it actually
            resolves to.
          </p>
        )}
      </ConfirmDialog>

      {canForce ? null : (
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
          A guarded removal can only be overridden with <span className="ref">quota:force</span>,
          which admin-owner holds.
        </p>
      )}
    </Card>
  );
}
